import { useState, useEffect, useCallback } from 'react';
import { Shield, Calendar, Filter, Search, X, AlertTriangle, CheckCircle, XCircle, Clock, Download, Eye, Server, Lock, Globe2 } from 'lucide-react';
import { Layout } from '../../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import api from '../../api';
import { formatDateARG } from '../../lib/utils';

// ============================================
// COMPONENTE PRINCIPAL
// ============================================

const DnsSecurityChecks = () => {
  const [stats, setStats] = useState({
    axfr: { total: 0, critical: 0, warning: 0, ok: 0 },
    dnssec: { total: 0, critical: 0, warning: 0, ok: 0 },
    propagation: { total: 0, critical: 0, warning: 0, ok: 0 },
    total_checks: 0,
    total_critical: 0,
    total_warning: 0
  });
  const [checks, setChecks] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingChecks, setLoadingChecks] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // Detail Modal
  const [detailModal, setDetailModal] = useState({ open: false, check: null });
  
  // Defaults: última semana
  const defaultDateFrom = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  const defaultDateTo = new Date().toISOString().split('T')[0];
  
  const [filters, setFilters] = useState({
    date_from: defaultDateFrom,
    date_to: defaultDateTo,
    source_id: '',
    check_type: '',
    status: '',
    search: '',
    page: 1
  });

  // Fetch sources
  useEffect(() => {
    const fetchSources = async () => {
      try {
        const res = await api.get('/reports/dns-security/sources');
        setSources(res.data.sources || []);
      } catch (err) {
        console.error('Error fetching sources:', err);
      }
    };
    fetchSources();
  }, []);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/reports/dns-security/stats', {
        params: {
          date_from: filters.date_from,
          date_to: filters.date_to,
          source_id: filters.source_id || undefined
        }
      });
      setStats(res.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, [filters.date_from, filters.date_to, filters.source_id]);

  // Fetch checks
  const fetchChecks = useCallback(async () => {
    setLoadingChecks(true);
    try {
      const res = await api.get('/reports/dns-security/checks', {
        params: {
          date_from: filters.date_from,
          date_to: filters.date_to,
          source_id: filters.source_id || undefined,
          check_type: filters.check_type || undefined,
          status: filters.status || undefined,
          search: filters.search || undefined,
          page: filters.page,
          page_size: 50
        }
      });
      setChecks(res.data);
    } catch (err) {
      console.error('Error fetching checks:', err);
    } finally {
      setLoadingChecks(false);
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchChecks();
  }, [fetchChecks]);

  // Export to Excel
  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await api.get('/reports/dns-security/export', {
        params: {
          date_from: filters.date_from,
          date_to: filters.date_to,
          source_id: filters.source_id || undefined,
          check_type: filters.check_type || undefined,
          status: filters.status || undefined
        },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `dns_security_${filters.date_from}_${filters.date_to}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Error exporting:', err);
    } finally {
      setExporting(false);
    }
  };

  // Date range helpers
  const setDateRange = (days) => {
    setFilters(prev => ({
      ...prev,
      date_from: new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      date_to: new Date().toISOString().split('T')[0],
      page: 1
    }));
  };

  // Check type filter click
  const handleCheckTypeClick = (type) => {
    setFilters(prev => ({
      ...prev,
      check_type: prev.check_type === type ? '' : type,
      page: 1
    }));
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="dns-security-checks">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Shield className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">DNS Security Checks</h1>
              <p className="text-muted-foreground text-sm">Verificaciones de seguridad en zonas DNS</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting} data-testid="export-btn">
            <Download className="h-4 w-4 mr-2" />
            {exporting ? 'Exportando...' : 'Exportar Excel'}
          </Button>
        </div>

        {/* Check Type Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <CheckTypeCard
            title="AXFR (Zone Transfer)"
            description="Detecta servidores con transferencia de zona abierta"
            icon={Server}
            stats={stats.axfr}
            active={filters.check_type === 'axfr'}
            onClick={() => handleCheckTypeClick('axfr')}
          />
          <CheckTypeCard
            title="DNSSEC"
            description="Valida firmas digitales y cadena de confianza"
            icon={Lock}
            stats={stats.dnssec}
            active={filters.check_type === 'dnssec'}
            onClick={() => handleCheckTypeClick('dnssec')}
          />
          <CheckTypeCard
            title="Propagation"
            description="Compara respuestas entre resolvers públicos"
            icon={Globe2}
            stats={stats.propagation}
            active={filters.check_type === 'propagation'}
            onClick={() => handleCheckTypeClick('propagation')}
          />
        </div>

        {/* Filters */}
        <FiltersBar
          filters={filters}
          setFilters={setFilters}
          sources={sources}
          setDateRange={setDateRange}
        />

        {/* Checks Table */}
        <ChecksTable
          checks={checks}
          loading={loading || loadingChecks}
          filters={filters}
          setFilters={setFilters}
          onViewDetail={(check) => setDetailModal({ open: true, check })}
        />

        {/* Detail Modal */}
        <DetailModal
          open={detailModal.open}
          onClose={() => setDetailModal({ open: false, check: null })}
          check={detailModal.check}
        />
      </div>
    </Layout>
  );
};


// ============================================
// CHECK TYPE CARD
// ============================================

const CheckTypeCard = ({ title, description, icon: Icon, stats, active, onClick }) => {
  const hasCritical = stats.critical > 0;
  const hasWarning = stats.warning > 0;
  
  return (
    <Card
      className={`card-subtle cursor-pointer transition-all hover:scale-[1.02] ${active ? 'ring-2 ring-offset-2 ring-offset-background ring-primary' : ''}`}
      onClick={onClick}
      data-testid={`check-type-card-${title.toLowerCase().split(' ')[0]}`}
    >
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-primary" />
            {title}
          </span>
          {hasCritical && (
            <Badge variant="outline" className="bg-red-500/10 text-red-400 border-red-500/30">
              {stats.critical} critical
            </Badge>
          )}
          {!hasCritical && hasWarning && (
            <Badge variant="outline" className="bg-yellow-500/10 text-yellow-400 border-yellow-500/30">
              {stats.warning} warning
            </Badge>
          )}
          {!hasCritical && !hasWarning && stats.total > 0 && (
            <Badge variant="outline" className="bg-green-500/10 text-green-400 border-green-500/30">
              OK
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-muted-foreground mb-2">{description}</p>
        <div className="flex items-center gap-4 text-sm">
          <span className="flex items-center gap-1">
            <span className="font-bold">{stats.total}</span>
            <span className="text-muted-foreground">checks</span>
          </span>
          {stats.critical > 0 && (
            <span className="flex items-center gap-1 text-red-400">
              <XCircle className="h-3 w-3" /> {stats.critical}
            </span>
          )}
          {stats.warning > 0 && (
            <span className="flex items-center gap-1 text-yellow-400">
              <AlertTriangle className="h-3 w-3" /> {stats.warning}
            </span>
          )}
          {stats.ok > 0 && (
            <span className="flex items-center gap-1 text-green-400">
              <CheckCircle className="h-3 w-3" /> {stats.ok}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};


// ============================================
// FILTERS BAR
// ============================================

const FiltersBar = ({ filters, setFilters, sources, setDateRange }) => {
  return (
    <Card className="card-subtle">
      <CardContent className="p-4">
        <div className="flex flex-wrap items-center gap-4">
          {/* Icon + Label */}
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Filtros:</span>
          </div>

          {/* Date Range */}
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <Input
              type="date"
              value={filters.date_from}
              onChange={(e) => setFilters(prev => ({ ...prev, date_from: e.target.value, page: 1 }))}
              className="h-9 w-36"
            />
            <span className="text-muted-foreground">a</span>
            <Input
              type="date"
              value={filters.date_to}
              onChange={(e) => setFilters(prev => ({ ...prev, date_to: e.target.value, page: 1 }))}
              className="h-9 w-36"
            />
          </div>

          {/* Quick Date Buttons */}
          <div className="flex items-center gap-1 border-l pl-3">
            <Button variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={() => setDateRange(7)}>
              7 días
            </Button>
            <Button variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={() => setDateRange(30)}>
              1 mes
            </Button>
            <Button variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={() => setDateRange(90)}>
              3 meses
            </Button>
          </div>

          {/* Source Dropdown */}
          <Select
            value={filters.source_id || 'all'}
            onValueChange={(val) => setFilters(prev => ({ ...prev, source_id: val === 'all' ? '' : val, page: 1 }))}
          >
            <SelectTrigger className="h-9 w-48">
              <SelectValue placeholder="Todas las fuentes" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las fuentes</SelectItem>
              {sources.map(s => (
                <SelectItem key={s.id} value={String(s.id)}>{s.name} ({s.domain})</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Status Dropdown */}
          <Select
            value={filters.status || 'all'}
            onValueChange={(val) => setFilters(prev => ({ ...prev, status: val === 'all' ? '' : val, page: 1 }))}
          >
            <SelectTrigger className="h-9 w-36">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
              <SelectItem value="warning">Warning</SelectItem>
              <SelectItem value="ok">OK</SelectItem>
              <SelectItem value="error">Error</SelectItem>
            </SelectContent>
          </Select>

          {/* Search */}
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar target..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value, page: 1 }))}
              className="h-9 w-48"
            />
          </div>

          {/* Clear Filters */}
          {(filters.search || filters.check_type || filters.status || filters.source_id) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setFilters(prev => ({ ...prev, search: '', check_type: '', status: '', source_id: '', page: 1 }))}
            >
              <X className="h-4 w-4 mr-1" /> Limpiar
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};


// ============================================
// CHECKS TABLE
// ============================================

const ChecksTable = ({ checks, loading, filters, setFilters, onViewDetail }) => {
  const getStatusBadge = (status) => {
    const styles = {
      critical: 'bg-red-500/10 text-red-400 border-red-500/30',
      warning: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30',
      ok: 'bg-green-500/10 text-green-400 border-green-500/30',
      error: 'bg-gray-500/10 text-gray-400 border-gray-500/30',
    };
    const icons = {
      critical: <XCircle className="h-3 w-3 mr-1" />,
      warning: <AlertTriangle className="h-3 w-3 mr-1" />,
      ok: <CheckCircle className="h-3 w-3 mr-1" />,
      error: <XCircle className="h-3 w-3 mr-1" />,
    };
    return (
      <Badge variant="outline" className={`${styles[status]} flex items-center`}>
        {icons[status]}
        {status.toUpperCase()}
      </Badge>
    );
  };

  const getCheckTypeBadge = (checkType) => {
    const labels = {
      axfr: 'AXFR',
      dnssec: 'DNSSEC',
      propagation: 'Propagation'
    };
    const colors = {
      axfr: 'bg-purple-500/10 text-purple-400 border-purple-500/30',
      dnssec: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
      propagation: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30'
    };
    return (
      <Badge variant="outline" className={colors[checkType]}>
        {labels[checkType]}
      </Badge>
    );
  };

  return (
    <Card className="card-subtle">
      <CardHeader>
        <CardTitle className="text-lg flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            Security Checks ({checks.total})
          </span>
          <span className="text-sm font-normal text-muted-foreground">
            Página {checks.page} de {checks.pages}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-8 text-muted-foreground">Cargando...</div>
        ) : checks.items.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No hay checks DNS en el período seleccionado con los filtros actuales
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="checks-table">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left p-3">Fecha</th>
                    <th className="text-left p-3">Source</th>
                    <th className="text-left p-3">Tipo</th>
                    <th className="text-left p-3">Target</th>
                    <th className="text-left p-3">Estado</th>
                    <th className="text-left p-3">Resumen</th>
                    <th className="text-center p-3">Detalle</th>
                  </tr>
                </thead>
                <tbody>
                  {checks.items.map((item) => (
                    <tr
                      key={item.id}
                      className="border-b border-border/50 hover:bg-muted/30"
                      data-testid={`check-row-${item.id}`}
                    >
                      <td className="p-3 text-muted-foreground whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatDateARG(item.checked_at)}
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="text-xs">
                          <div className="font-medium">{item.source_name}</div>
                          <div className="text-muted-foreground">{item.source_domain}</div>
                        </div>
                      </td>
                      <td className="p-3">{getCheckTypeBadge(item.check_type)}</td>
                      <td className="p-3 font-mono text-xs max-w-[200px] truncate" title={item.target}>
                        {item.target}
                      </td>
                      <td className="p-3">{getStatusBadge(item.status)}</td>
                      <td className="p-3 text-xs text-muted-foreground max-w-[300px] truncate" title={item.summary}>
                        {item.summary || '-'}
                      </td>
                      <td className="p-3 text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onViewDetail(item)}
                          title="Ver detalle"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {checks.pages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t border-border/50">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={filters.page <= 1}
                  onClick={() => setFilters(prev => ({ ...prev, page: prev.page - 1 }))}
                >
                  Anterior
                </Button>
                <span className="text-sm text-muted-foreground">
                  Página {filters.page} de {checks.pages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={filters.page >= checks.pages}
                  onClick={() => setFilters(prev => ({ ...prev, page: prev.page + 1 }))}
                >
                  Siguiente
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};


// ============================================
// DETAIL MODAL
// ============================================

const DetailModal = ({ open, onClose, check }) => {
  if (!check) return null;

  const renderPayload = (checkType, payload) => {
    if (!payload) return <p className="text-muted-foreground">Sin datos adicionales</p>;

    switch (checkType) {
      case 'axfr':
        return (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-muted-foreground">NS Hostname</label>
                <p className="font-mono text-sm">{payload.ns_hostname || '-'}</p>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">NS IP</label>
                <p className="font-mono text-sm">{payload.ns_ip || '-'}</p>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">AXFR Status</label>
                <p className={`font-medium ${payload.axfr_status === 'allowed' ? 'text-red-400' : payload.axfr_status === 'refused' ? 'text-green-400' : 'text-yellow-400'}`}>
                  {payload.axfr_status?.toUpperCase() || '-'}
                </p>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Records Leaked</label>
                <p className={`font-bold ${payload.records_leaked > 0 ? 'text-red-400' : ''}`}>
                  {payload.records_leaked || 0}
                </p>
              </div>
            </div>
            {payload.sample_records && payload.sample_records.length > 0 && (
              <div>
                <label className="text-xs text-muted-foreground">Sample Records (leaked)</label>
                <div className="mt-1 p-2 bg-muted/50 rounded text-xs font-mono max-h-32 overflow-y-auto">
                  {payload.sample_records.slice(0, 10).map((r, i) => (
                    <div key={i} className="text-red-400">{r}</div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case 'dnssec':
        return (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-muted-foreground">Is Signed</label>
                <p className={payload.is_signed ? 'text-green-400' : 'text-yellow-400'}>
                  {payload.is_signed ? 'YES' : 'NO'}
                </p>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Has DS at Parent</label>
                <p className={payload.has_ds_at_parent ? 'text-green-400' : 'text-red-400'}>
                  {payload.has_ds_at_parent ? 'YES' : 'NO'}
                </p>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Chain of Trust</label>
                <p className={payload.chain_of_trust_valid ? 'text-green-400' : 'text-red-400'}>
                  {payload.chain_of_trust_valid ? 'VALID' : 'BROKEN'}
                </p>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Days Until Expiry</label>
                <p className={`font-bold ${payload.days_until_expiry < 7 ? 'text-red-400' : payload.days_until_expiry < 30 ? 'text-yellow-400' : 'text-green-400'}`}>
                  {payload.days_until_expiry ?? '-'}
                </p>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">KSK Algorithm</label>
                <p className="font-mono text-sm">{payload.ksk_algorithm || '-'}</p>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">ZSK Algorithm</label>
                <p className="font-mono text-sm">{payload.zsk_algorithm || '-'}</p>
              </div>
            </div>
            {payload.warnings && payload.warnings.length > 0 && (
              <div>
                <label className="text-xs text-muted-foreground">Warnings</label>
                <div className="mt-1 space-y-1">
                  {payload.warnings.map((w, i) => (
                    <Badge key={i} variant="outline" className="bg-yellow-500/10 text-yellow-400 border-yellow-500/30 mr-1">
                      {w}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case 'propagation':
        return (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-muted-foreground">Record FQDN</label>
                <p className="font-mono text-sm">{payload.record_fqdn || '-'}</p>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Record Type</label>
                <p className="font-mono text-sm">{payload.record_type || '-'}</p>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Is Diverged</label>
                <p className={payload.is_diverged ? 'text-red-400' : 'text-green-400'}>
                  {payload.is_diverged ? 'YES' : 'NO'}
                </p>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Unique Answers</label>
                <p className={`font-bold ${payload.unique_answers > 1 ? 'text-yellow-400' : ''}`}>
                  {payload.unique_answers || '-'}
                </p>
              </div>
            </div>
            {payload.resolvers && (
              <div>
                <label className="text-xs text-muted-foreground">Resolver Responses</label>
                <div className="mt-1 space-y-2">
                  {Object.entries(payload.resolvers).map(([resolver, data]) => {
                    const isDivergent = payload.divergent_resolvers?.includes(resolver);
                    return (
                      <div key={resolver} className={`p-2 rounded ${isDivergent ? 'bg-red-500/10 border border-red-500/30' : 'bg-muted/50'}`}>
                        <div className="flex items-center justify-between">
                          <span className="font-medium capitalize">{resolver}</span>
                          <span className="text-xs text-muted-foreground">{data.response_time_ms}ms</span>
                        </div>
                        <div className="font-mono text-xs mt-1">
                          {data.value?.join(', ') || data.error || '-'}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {payload.divergent_resolvers && payload.divergent_resolvers.length > 0 && (
              <div>
                <label className="text-xs text-muted-foreground">Divergent Resolvers</label>
                <div className="mt-1">
                  {payload.divergent_resolvers.map((r, i) => (
                    <Badge key={i} variant="outline" className="bg-red-500/10 text-red-400 border-red-500/30 mr-1">
                      {r}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      default:
        return (
          <pre className="text-xs bg-muted/50 p-3 rounded overflow-auto max-h-64">
            {JSON.stringify(payload, null, 2)}
          </pre>
        );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            Detalle del Check
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto space-y-4">
          {/* Header Info */}
          <div className="grid grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg">
            <div>
              <label className="text-xs text-muted-foreground">Source</label>
              <p className="font-medium">{check.source_name}</p>
              <p className="text-xs text-muted-foreground">{check.source_domain}</p>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Target</label>
              <p className="font-mono text-sm">{check.target}</p>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Tipo</label>
              <p className="font-medium">{check.check_type?.toUpperCase()}</p>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Fecha</label>
              <p>{formatDateARG(check.checked_at)}</p>
            </div>
          </div>

          {/* Summary */}
          {check.summary && (
            <div className="p-4 bg-muted/30 rounded-lg">
              <label className="text-xs text-muted-foreground">Resumen</label>
              <p className="mt-1">{check.summary}</p>
            </div>
          )}

          {/* Payload Details */}
          <div className="p-4 bg-muted/30 rounded-lg">
            <label className="text-xs text-muted-foreground mb-3 block">Detalles Técnicos</label>
            {renderPayload(check.check_type, check.payload)}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};


export default DnsSecurityChecks;
